import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, Trash2, Loader2, RefreshCw,
  MapPin, Boxes, Search as SearchIcon, FileText, Play, RotateCcw, Unlock, X,
  ArrowDownToLine, Volume2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { beep, fmtDT } from "@/lib/sound";

const LIST_COLORS = [
  "bg-sky-100 text-sky-900 border-sky-300",
  "bg-amber-100 text-amber-900 border-amber-300",
  "bg-violet-100 text-violet-900 border-violet-300",
  "bg-rose-100 text-rose-900 border-rose-300",
  "bg-emerald-100 text-emerald-900 border-emerald-300",
  "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
  "bg-cyan-100 text-cyan-900 border-cyan-300",
  "bg-lime-100 text-lime-900 border-lime-300",
  "bg-orange-100 text-orange-900 border-orange-300",
  "bg-indigo-100 text-indigo-900 border-indigo-300",
];
const colorFor = (lista) => LIST_COLORS[(Number(lista) - 1) % LIST_COLORS.length] || LIST_COLORS[0];
const normKey = (s) => (s || "").replace(/\s+/g, "").toLowerCase();

function QtdEditor({ initial, onConfirm, onCancel, testid }) {
  const ref = useRef(null);
  const [v, setV] = useState(initial || "");
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const confirm = () => {
    const n = parseInt(v, 10);
    if (!n || n <= 0) { toast.error("Informe uma quantidade maior que zero"); ref.current?.focus(); return; }
    onConfirm(n);
  };
  return (
    <div className="flex items-center gap-2" data-testid={`${testid}-editor`}>
      <input ref={ref} type="number" min={1} inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") confirm(); if (e.key === "Escape") onCancel(); }}
        data-testid={`${testid}-input`} placeholder="Qtd"
        className="w-24 h-12 text-2xl font-mono font-bold text-center border-2 border-[#F2B01E] rounded-md text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#F2B01E]" />
      <button data-testid={`${testid}-confirm`} onClick={confirm} className="h-12 px-4 bg-[#F2B01E] hover:bg-[#B8860B] text-white font-bold rounded-md text-sm">Confirmar</button>
      <button onClick={onCancel} className="h-12 w-10 flex items-center justify-center text-[#5A6B62] hover:bg-[#F1F5F2] rounded-md"><X className="w-4 h-4" /></button>
    </div>
  );
}

function PulmaoPanel({ info, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 900); return () => clearTimeout(t); }, [info, onClose]);
  if (!info) return null;
  const { codigo, nome, pulmao } = info;
  return (
    <div className="fixed bottom-3 right-3 z-50 w-[220px] md:w-[300px] bg-[#0B1F17] text-white rounded-lg shadow-2xl border border-[#2DC26B] p-2.5 md:p-3 animate-in slide-in-from-bottom-4" data-testid="onda-pulmao-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[#2DC26B] text-[9px] md:text-[10px] font-bold tracking-widest uppercase"><Volume2 className="w-3 h-3" /> Pulmão</div>
        <button onClick={onClose} data-testid="onda-pulmao-panel-close" className="text-slate-300 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="font-mono text-[11px] text-slate-300 truncate">{codigo} · {nome}</div>
      {pulmao?.mensagem ? <div className="mt-1.5 text-[#FCA5A5] font-semibold text-xs" data-testid="onda-pulmao-panel-msg">{pulmao.mensagem}</div> : null}
      {pulmao?.localizacoes?.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {pulmao.localizacoes.slice(0, 3).map((l) => (
            <div key={l.endereco} className="flex justify-between font-mono text-sm">
              <span className={`font-bold ${l.saldo <= 0 ? "line-through text-[#FCA5A5]" : "text-white"}`}>{l.endereco}</span>
              <span className="text-slate-300 text-xs">saldo {l.saldo}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 h-0.5 bg-[#1A3A2C] rounded overflow-hidden"><div className="h-full bg-[#2DC26B] animate-[shrink_0.9s_linear_forwards]" style={{ width: "100%" }} /></div>
      <style>{`@keyframes shrink{from{width:100%}to{width:0}}`}</style>
    </div>
  );
}

function ListaRow({ grupo, produto, lista, qtd, onBip, onClaim, onLiberar, onReabrir }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState(false);
  const conf = produto.conferencia_por_lista?.[lista] || null;
  const st = conf?.status || null;
  const claim = produto.claim_por_lista?.[lista] || null;
  useEffect(() => {
    if (!conf?.at) { setRecent(false); return; }
    const age = Date.now() - new Date(conf.at).getTime();
    if (age > 5000) { setRecent(false); return; }
    setRecent(true);
    const t = setTimeout(() => setRecent(false), 5000 - age);
    return () => clearTimeout(t);
  }, [conf?.at]);
  const active = (s) => recent && st === s;
  const tid = `onda-${produto.codigo}-l${lista}-${normKey(grupo.endereco)}`;

  const act = async (status, quantidade) => {
    setBusy(true);
    try { await onBip({ lista, codigo: produto.codigo, endereco: grupo.endereco, status, quantidade }); setEditing(false); }
    finally { setBusy(false); }
  };
  const btn = (s, label, cls, activeOn) =>
    <button key={s} data-testid={`${tid}-${s.toLowerCase()}`} disabled={busy} onClick={() => (s === "QTD" ? setEditing(true) : act(s))}
      className={`h-11 min-w-[72px] px-3 text-xs font-bold tracking-wider rounded-md border-2 transition-colors disabled:opacity-60 ${activeOn ? cls.on : cls.off}`}>{label}</button>;

  const callAux = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-5 py-4 border-t border-[#F1F5F2]" data-testid={tid}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-bold ${colorFor(lista)}`}>
            <FileText className="w-3 h-3" /> Lista {lista} · {qtd} un
          </span>
          {claim && !st && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]">
              Em andamento · {claim.usuario}
            </span>
          )}
        </div>
        <div className="font-mono text-sm font-semibold text-[#0F6B3A] mt-1">{produto.codigo}</div>
        <div className="font-semibold text-[#0F2A1F] truncate">{produto.nome || "—"}</div>
        <div className="font-mono text-xs text-[#5A6B62] mt-0.5 flex flex-wrap items-center gap-x-2">
          <span>Sistema: <b className="text-[#0F2A1F]">{produto.fisica}</b></span>
          <span>Alocado: <b className="text-[#0F2A1F]">{produto.alocado}</b></span>
          <span>Disponível: <b className={produto.disponivel <= 0 ? "text-[#B91C1C]" : "text-[#0F2A1F]"}>{produto.disponivel}</b></span>
          {produto.sem_estoque && <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#B91C1C] text-[10px] font-bold uppercase" data-testid={`${tid}-sem-estoque`}>Sem estoque</span>}
          {produto.verificar_loja && <span className="text-[#B8860B] text-[10px] font-bold uppercase">· verificar na loja</span>}
        </div>
        {conf && (
          <div className="font-mono text-[11px] text-[#8A9A90] mt-0.5" data-testid={`${tid}-conf-info`}>
            <span className={`font-bold ${st === "OK" ? "text-[#047857]" : st === "REPOR" ? "text-[#B91C1C]" : "text-[#9A6B0A]"}`}>{st}{st === "QTD" ? ` · ${conf.quantidade}` : ""}</span>
            {" · "}{conf.usuario} · {fmtDT(conf.at)}
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-2">
        {editing ? (
          <QtdEditor initial={conf?.quantidade} onConfirm={(n) => act("QTD", n)} onCancel={() => setEditing(false)} testid={`${tid}-qtd`} />
        ) : (
          <div className="flex gap-2">
            {btn("OK", "OK", { on: "bg-[#10B981] border-[#10B981] text-white", off: "bg-white border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" }, active("OK"))}
            {btn("REPOR", "REPOR", { on: "bg-[#EF4444] border-[#EF4444] text-white", off: "bg-white border-[#FECACA] text-[#B91C1C] hover:bg-[#FEF2F2]" }, active("REPOR"))}
            {btn("QTD", st === "QTD" ? `QTD · ${conf.quantidade}` : "QTD", { on: "bg-[#F2B01E] border-[#F2B01E] text-white", off: "bg-white border-[#FCE6A3] text-[#9A6B0A] hover:bg-[#FFFBEE]" }, active("QTD"))}
          </div>
        )}
        <div className="flex gap-2">
          {!st && !claim && (
            <button data-testid={`${tid}-iniciar`} disabled={busy} onClick={() => callAux(() => onClaim(grupo, produto, lista))}
              className="h-8 px-2 border border-[#0F6B3A]/40 rounded text-[#0F6B3A] text-xs font-semibold hover:bg-[#EAF7EF] disabled:opacity-60 flex items-center gap-1">
              <Play className="w-3 h-3" /> Iniciar
            </button>
          )}
          {!st && claim && (
            <button data-testid={`${tid}-liberar`} disabled={busy} onClick={() => callAux(() => onLiberar(grupo, produto, lista))}
              className="h-8 px-2 border border-[#D9E3DC] rounded text-[#5A6B62] text-xs font-semibold hover:bg-[#F1F5F2] disabled:opacity-60 flex items-center gap-1">
              <Unlock className="w-3 h-3" /> Liberar
            </button>
          )}
          {st && (
            <button data-testid={`${tid}-reabrir`} disabled={busy} onClick={() => callAux(() => onReabrir(grupo, produto, lista))}
              className="h-8 px-2 border border-[#D9E3DC] rounded text-[#0F2A1F] text-xs font-semibold hover:bg-[#F1F5F2] disabled:opacity-60 flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Reabrir
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OndaReposicao() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState("");
  const [rackFilter, setRackFilter] = useState("");
  const [status, setStatus] = useState("pendentes");
  const [panel, setPanel] = useState(null);
  const [autoNext, setAutoNext] = useState(() => localStorage.getItem("onda_auto_next") !== "0");
  const pendingScroll = useRef(null);
  const fileRef = useRef(null);
  const toggleAutoNext = () => {
    const v = !autoNext;
    setAutoNext(v);
    localStorage.setItem("onda_auto_next", v ? "1" : "0");
  };

  const fetchOnda = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (rackFilter) params.rack = rackFilter;
      if (status && status !== "todos") params.status = status;
      const r = await api.get("/picklist/onda", { params });
      setData(r.data);
    } catch {
      toast.error("Erro ao carregar onda consolidada");
    } finally { setLoading(false); }
  }, [q, rackFilter, status]);

  useEffect(() => { fetchOnda(); }, [fetchOnda]);

  // Flatten grupos × produtos × listas para calcular "próximo pendente"
  const flat = useMemo(() => {
    const out = [];
    (data?.grupos || []).forEach((g) => {
      g.produtos.forEach((p) => {
        p.listas.forEach((l) => out.push({ g, p, lista: l.lista }));
      });
    });
    return out;
  }, [data]);

  // Após re-fetch, rola para o próximo pendente marcado
  useEffect(() => {
    const target = pendingScroll.current;
    if (!target) return;
    pendingScroll.current = null;
    const el = document.querySelector(`[data-testid="${target}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("pk-next-highlight");
    setTimeout(() => el.classList.remove("pk-next-highlight"), 1600);
  }, [data]);

  const importarLote = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > 10) { toast.error("Máximo 10 listas por onda"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await api.post("/picklist/onda/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.total} lista(s) iniciada(s) para processamento`);
      setTimeout(() => fetchOnda(), 2500);
      setTimeout(() => fetchOnda(), 5500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao importar lote");
    } finally { setImporting(false); }
  };

  const limparOnda = async () => {
    if (!window.confirm("Remover TODAS as listas da onda atual? Reposições em andamento permanecem.")) return;
    try { await api.delete("/picklist/onda"); toast.success("Onda limpa"); fetchOnda(); }
    catch { toast.error("Erro ao limpar"); }
  };

  const bipar = async (payload) => {
    beep("scan");
    const ekey = normKey(payload.endereco);
    // Define próximo pendente antes do refetch, usando snapshot atual
    let nextTid = null;
    if (autoNext) {
      const idx = flat.findIndex((x) =>
        x.p.codigo === payload.codigo && x.lista === payload.lista && normKey(x.g.endereco) === ekey
      );
      if (idx >= 0) {
        const next = flat.slice(idx + 1).find((x) => !x.p.conferencia_por_lista?.[x.lista]);
        if (next) nextTid = `onda-${next.p.codigo}-l${next.lista}-${normKey(next.g.endereco)}`;
      }
    }
    try {
      const { data: r } = await api.post("/picklist/conferir", { ...payload, origem: "onda" });
      if (payload.status === "OK") {
        beep("scan-ok");
        toast.success(r.removida ? "OK · reposição removida (correção)" : "Conferido OK");
      } else {
        beep("alert");
        setPanel({ codigo: payload.codigo, nome: r.reposicao?.descricao || "", pulmao: r.pulmao });
        const mensagem = payload.status === "REPOR"
          ? `Enviado para REPOR ONDA (1 SKU)`
          : `Enviado para REPOR ONDA · ${payload.quantidade} un.`;
        toast.success(mensagem);
      }
      if (nextTid) pendingScroll.current = nextTid;
      await fetchOnda();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao conferir"); }
  };

  const onClaim = async (g, p, l) => {
    try { await api.post("/picklist/onda/claim", { lista: l, codigo: p.codigo, endereco: g.endereco }); toast.success(`Lista ${l} em andamento`); await fetchOnda(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const onLiberar = async (g, p, l) => {
    try { await api.post("/picklist/onda/liberar", { lista: l, codigo: p.codigo, endereco: g.endereco }); toast.success(`Lista ${l} liberada`); await fetchOnda(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const onReabrir = async (g, p, l) => {
    if (!window.confirm(`Reabrir Lista ${l} do ${p.codigo}? A conferência será apagada e uma eventual reposição ativa também.`)) return;
    try { await api.post("/picklist/onda/reabrir", { lista: l, codigo: p.codigo, endereco: g.endereco }); toast.success(`Lista ${l} reaberta`); await fetchOnda(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const prog = data?.progresso || { conferidos: 0, total: 0, pct: 0 };

  return (
    <div data-testid="onda-reposicao" className="space-y-3">
      <div className="bg-[#0F2A1F] text-white rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-[#8DBDA2]">Onda de Reposição</div>
          <div className="text-lg font-bold">
            {data?.listas?.length || 0} lista(s) · {prog.total} itens para bipar · <span className="text-[#2DC26B]">{prog.pct}% conferidos</span>
          </div>
          <div className="h-1.5 w-56 mt-1 bg-[#1A3A2C] rounded-full overflow-hidden">
            <div className="h-full bg-[#2DC26B] transition-[width] duration-500" style={{ width: `${prog.pct}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="onda-auto-next-toggle" onClick={toggleAutoNext}
            title="Rolar automaticamente para o próximo item pendente após bipar"
            className={`h-10 inline-flex items-center gap-2 px-3 rounded-md border text-xs font-bold transition-colors ${autoNext ? "bg-[#2DC26B] border-[#2DC26B] text-white" : "bg-white border-[#D9E3DC] text-[#5A6B62]"}`}>
            <ArrowDownToLine className="w-3.5 h-3.5" /> Próximo automático {autoNext ? "ligado" : "desligado"}
          </button>
          <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" data-testid="onda-import-input"
            onChange={(e) => { importarLote(e.target.files); e.target.value = ""; }} />
          <button data-testid="btn-importar-onda" onClick={() => fileRef.current?.click()} disabled={importing}
            className="h-10 px-4 bg-[#2DC26B] hover:bg-[#25A85B] disabled:opacity-60 text-white font-bold rounded-md flex items-center gap-2 text-sm">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importar Lote (vários PDFs)
          </button>
          <button data-testid="btn-limpar-onda" onClick={limparOnda} className="h-10 px-3 bg-white/10 hover:bg-white/20 text-white rounded-md flex items-center gap-1 text-sm">
            <Trash2 className="w-4 h-4" /> Limpar Onda
          </button>
        </div>
      </div>

      {data?.listas?.length > 0 && (
        <div className="bg-white border border-[#D9E3DC] rounded-lg p-3 flex flex-wrap gap-2" data-testid="onda-chips-listas">
          {data.listas.map((l) => (
            <div key={l.lista} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-semibold ${colorFor(l.lista)}`}>
              <FileText className="w-3 h-3" /> Lista {l.lista}: {l.total_itens || 0} itens / {l.total_unidades || 0} un
              {l.lote && <span className="opacity-70">· Lote {l.lote}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#D9E3DC] rounded-lg p-3 grid md:grid-cols-6 gap-2">
        <div className="relative md:col-span-3">
          <SearchIcon className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="onda-busca" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-10 pl-9 pr-3 border border-[#D9E3DC] rounded-md text-sm" placeholder="Buscar código ou descrição…" />
        </div>
        <select data-testid="onda-filtro-rack" value={rackFilter} onChange={(e) => setRackFilter(e.target.value)} className="h-10 px-2 border border-[#D9E3DC] rounded-md text-sm">
          <option value="">Todos racks (separação)</option>
          {(data?.racks || []).map((r) => <option key={r} value={r}>Rack {r}</option>)}
        </select>
        <select data-testid="onda-filtro-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-2 border border-[#D9E3DC] rounded-md text-sm">
          <option value="pendentes">Pendentes</option>
          <option value="EM_ANDAMENTO">Em andamento</option>
          <option value="OK">Conferidos (OK)</option>
          <option value="REPOR">Marcados REPOR</option>
          <option value="QTD">Marcados QTD</option>
          <option value="todos">Todos</option>
        </select>
        <button data-testid="onda-refresh" onClick={fetchOnda} className="h-10 px-3 bg-[#0F6B3A] hover:bg-[#0B5530] text-white rounded-md font-semibold text-sm flex items-center justify-center gap-1">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      {loading && (
        <div className="text-center text-slate-400 py-8" data-testid="onda-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      )}
      {!loading && (!data?.grupos || data.grupos.length === 0) && (
        <div className="bg-white border border-dashed border-[#D9E3DC] rounded-lg p-10 text-center text-[#8A9A90]" data-testid="onda-vazia">
          <Boxes className="w-10 h-10 mx-auto opacity-30 mb-2" />
          {(data?.listas?.length || 0) === 0 ? "Importe listas (PDF) para montar sua onda" : "Nenhum item encontrado"}
        </div>
      )}
      {!loading && data?.grupos?.length > 0 && (
        <div className="space-y-4" data-testid="onda-grupos">
          {data.grupos.map((g, gi) => {
            const totalLinhas = g.produtos.reduce((acc, p) => acc + (p.listas?.length || 0), 0);
            return (
              <div key={gi} className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm" data-testid={`onda-grupo-${g.rack}-${g.linha}-${g.coluna}`}>
                <div className="flex items-center gap-3 px-5 py-3 bg-[#F4F8F5] rounded-t-lg">
                  <MapPin className="w-4 h-4 text-[#0F6B3A]" />
                  <span className="font-mono text-xl font-bold text-[#0F2A1F] tracking-wide">{g.endereco}</span>
                  <span className="font-mono text-[11px] text-[#8A9A90]">{totalLinhas} item(s) · {g.produtos.length} SKU(s)</span>
                </div>
                {g.produtos.map((p) =>
                  p.listas.map((l) => (
                    <ListaRow
                      key={`${p.codigo}-${l.lista}`}
                      grupo={g}
                      produto={p}
                      lista={l.lista}
                      qtd={l.qtd}
                      onBip={bipar}
                      onClaim={onClaim}
                      onLiberar={onLiberar}
                      onReabrir={onReabrir}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      <PulmaoPanel info={panel} onClose={() => setPanel(null)} />
    </div>
  );
}
